/**
 * LoadingHint — l'indicateur de chargement DISCRET et DIFFÉRÉ (retapé au lot 4, 01-11-2026 :
 * il rend le `Spinner` de la librairie, et peut dire ce qu'il attend).
 *
 * Il ne montre RIEN pendant `delayMs` (400 ms par défaut) : un chargement rapide n'affiche aucun
 * placeholder, le contenu apparaît directement — sensation « instantané », zéro flash. Au-delà,
 * un petit anneau calme et centré signale que ça travaille. Jamais un gros squelette animé
 * (jugé « pas fluide » : il attire l'œil sur l'attente, et son remplacement par le contenu fait
 * un saut visible) — le `Squelette` est pour une liste dont on connaît la forme.
 *
 *   <LoadingHint />
 *   <LoadingHint texte="Chargement des comptes…" delayMs={200} />
 */
import { useEffect, useState } from "react";

import { Spinner } from "./Spinner";
import { cn } from "./utils";

export interface LoadingHintProps {
  /** Le délai avant d'apparaître. Défaut 400 ms. */
  delayMs?: number;
  /** À côté de l'anneau, en gris. */
  texte?: string;
  className?: string;
}

export function LoadingHint({ delayMs = 400, texte, className }: LoadingHintProps) {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const id = window.setTimeout(() => setShow(true), delayMs);
    return () => window.clearTimeout(id);
  }, [delayMs]);
  if (!show) return null;
  return (
    <div className={cn("flex items-center justify-center gap-3 py-10", className)} role="status" aria-live="polite" aria-busy="true" aria-label={texte ?? "Chargement"}>
      <Spinner decoratif />
      {texte && <span className="text-[13px] text-muted">{texte}</span>}
    </div>
  );
}
